import { Injectable } from '@angular/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/shared/services/auth.service';
import { DeviceApp } from '../device-app.info';
import { ApiInterfaceService } from './api-interface.service';
import { NgxCableService } from './ngx-cable.service';

@Injectable({
  providedIn: 'root',
})
export class ActionCableService {
  readonly host: string = `${environment.actionCableUrl()}/cable`;

  private actionCableIsCurrentlyConnecting = false; // true while action cable is attempting to connect

  constructor(
    private ngcable: NgxCableService,
    private deviceApp: DeviceApp,
    private authService: AuthService,
    private api: ApiInterfaceService,
  ) {
    this.authService.userAuthStatusSubject.pipe(distinctUntilChanged()).subscribe((isLoggedIn) => {
      isLoggedIn ? this.connect() : this.disconnect();
    });
  }

  /**
   * Connects to the host.
   * This needs to be called right after login and everytime when page reloads to establish the live connection.
   */
  private async connect() {
    if (!this.ngcable.isOpen() && !this.actionCableIsCurrentlyConnecting) {
      this.actionCableIsCurrentlyConnecting = true;
      await this.connectToHost();
      this.actionCableIsCurrentlyConnecting = false;
    }
  }

  /**
   * Disconnect from all subs.
   */
  disconnect(): void {
    // make sure the cable is actually open before we try to disconnect
    if (!this.ngcable.isOpen()) {
      return;
    }

    // unsub from all the subscriptions
    const subscriptions = this.ngcable.getSubscriptions();

    if (subscriptions?.length) {
      this.ngcable.unsubscribe(subscriptions);
    }

    // disconnect
    this.ngcable.disconnect();
  }

  /**
   * Remove/Unsubscribe a particular Channel Subscription.
   *
   * @param channel - A subscription to an ngCable broadcaster.
   */
  removeChannel(channel): void {
    this.ngcable.reject(channel);

    channel.unsubscribe();
  }

  /**
   * Connect to host.
   */
  private async connectToHost() {
    try {
      // Get action cable token used for establishing connection with action cable.
      const res = await (<any>(await this.api.get('/api/v1/user/cable_token.json', true)).toPromise());
      const token = res.token;

      await this.ngcable.connect(`${this.host}?token=${token}`);
    } catch (e) {
      console.log('action cable connection error', e);
    }
  }

  /**
   * Subscribe to a particular channel.
   *
   * @param channelName - Channel name to subscribe.
   * @param actionCableParams - Params to pass along if any.
   */
  async subscribeToChannel(channelName, actionCableParams = {}): Promise<any> {
    // make sure action cable is connected
    if (!this.ngcable.isOpen()) {
      // if a connection isn't there and there isn't one in progress, attempt to connect
      if (!this.actionCableIsCurrentlyConnecting) {
        await this.connect();
      } else {
        // if there is already one in progress, delay 5000 ms and then call this same function (will check if a connection is ready again)
        await new Promise((res) => setTimeout(res, 5000));
        return this.subscribeToChannel(channelName, actionCableParams);
      }
    }

    const info = await this.deviceApp.getInfo();
    const params = {
      channel: channelName,
      ...actionCableParams,
      room: '',
      mobile_info: info,
    };

    return this.ngcable.subscribe(params);
  }
}
